import { toast } from 'sonner';
import i18next from 'i18next';
import { useDocumentRevisionStore } from '@/store/document-revision.store.js';
import { toUploadProgressHandler } from '@/lib/upload/percent-from-axios-progress.js';

export class DocumentRevisionIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async getRevisions(docId, params = {}) {
        return this.service.getRevisions(docId, params);
    }

    async getActiveRevision(docId) {
        return this.service.getActiveRevision(docId);
    }

    async getRevisionById(docId, revId) {
        return this.service.getRevisionById(docId, revId);
    }

    async fetchRevisions(docId, params = {}) {
        if (docId == null || docId === '') {
            useDocumentRevisionStore.setState({
                revisions: [],
                totalCount: 0,
                loading: false,
                error: null,
            });
            return { data: [], total: 0 };
        }
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getRevisions(docId, params);
            useDocumentRevisionStore.setState({
                revisions: response.data || [],
                totalCount: response.total || 0,
                loading: false,
            });
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while fetching revisions',
                loading: false,
            });
            throw error;
        }
    }

    async fetchActiveRevision(docId) {
        if (docId == null || docId === '') {
            useDocumentRevisionStore.setState({
                activeRevision: null,
                loading: false,
                error: null,
            });
            return { data: null };
        }
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.getActiveRevision(docId);
            useDocumentRevisionStore.setState({
                activeRevision: response.data,
                loading: false,
            });
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while fetching the active revision',
                loading: false,
            });
            throw error;
        }
    }

    async createRevision(docId, data, file, onProgress) {
        const formData = new FormData();
        Object.keys(data || {}).forEach((key) => {
            const v = data[key];
            if (v === undefined || v === null || v === '') return;
            formData.append(key, v);
        });
        if (file) {
            formData.append('file', file);
        }

        useDocumentRevisionStore.setState({ uploading: true, error: null });
        try {
            const response = await this.service.createRevision(docId, formData, {
                onUploadProgress: toUploadProgressHandler(onProgress),
            });
            const store = useDocumentRevisionStore.getState();
            useDocumentRevisionStore.setState({
                revisions: [response.data, ...store.revisions],
                totalCount: store.totalCount + 1,
                uploading: false,
            });
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while creating the revision',
                uploading: false,
            });
            throw error;
        }
    }

    async updateRevisionContent(docId, revId, data, file) {
        const formData = new FormData();
        Object.keys(data || {}).forEach((key) => {
            const v = data[key];
            if (v === undefined || v === null || v === '') return;
            formData.append(key, v);
        });
        if (file) {
            formData.append('file', file);
        }
        useDocumentRevisionStore.setState({ uploading: true, error: null });
        try {
            const response = await this.service.updateRevisionContent(docId, revId, formData);
            useDocumentRevisionStore.setState((state) => ({
                revisions: state.revisions.map((rev) =>
                    rev._id === revId ? response.data : rev,
                ),
                uploading: false,
            }));
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while updating the revision',
                uploading: false,
            });
            throw error;
        }
    }

    async activateRevision(docId, revId) {
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.activateRevision(docId, revId);
            useDocumentRevisionStore.setState((state) => ({
                revisions: state.revisions.map((rev) => ({
                    ...rev,
                    isActive: rev._id === revId,
                })),
                activeRevision: response.data,
                loading: false,
            }));
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while activating the revision',
                loading: false,
            });
            throw error;
        }
    }

    async downloadRevision(docId, revId, fileName = 'document') {
        try {
            const blob = await this.service.downloadRevision(docId, revId);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            return { success: true };
        } catch (error) {
            toast.error(i18next.t('integrationToasts:couldNotDownloadFile', { defaultValue: 'Could not download file' }));
            throw error;
        }
    }

    async exportRevisionWithMetadata(docId, revId, payload, fileName = 'export.bin') {
        try {
            const blob = await this.service.exportRevisionWithMetadata(docId, revId, payload);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(
                i18next.t('reportOperationsDocument:messages.exportSuccess', {
                    defaultValue: 'Export started',
                }),
            );
            return { success: true };
        } catch (error) {
            toast.error(
                i18next.t('reportOperationsDocument:messages.exportFailed', {
                    defaultValue: 'Export failed',
                }),
            );
            return { success: false, error: error.message };
        }
    }

    async compareRevisions(docId, revId, targetRevId, fileName = 'comparison.docx', compareQuery = {}) {
        try {
            const blob = await this.service.compareRevisions(docId, revId, targetRevId, compareQuery);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            return { success: true };
        } catch (error) {
            toast.error(i18next.t('integrationToasts:couldNotGenerateComparisonReport', { defaultValue: 'Could not generate comparison report' }));
            throw error;
        }
    }

    async submitForApproval(docId, revId) {
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.submitForApproval(docId, revId);
            useDocumentRevisionStore.setState((state) => ({
                revisions: state.revisions.map((rev) =>
                    rev._id === revId ? response.data : rev,
                ),
                loading: false,
            }));
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while submitting for approval',
                loading: false,
            });
            throw error;
        }
    }

    async approveRevision(docId, revId, data) {
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.approveRevision(docId, revId, data);
            useDocumentRevisionStore.setState((state) => ({
                revisions: state.revisions.map((rev) =>
                    rev._id === revId ? response.data : rev,
                ),
                loading: false,
            }));
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'An error occurred while approving',
                loading: false,
            });
            throw error;
        }
    }

    async rejectRevision(docId, revId, data) {
        useDocumentRevisionStore.setState({ loading: true, error: null });
        try {
            const response = await this.service.rejectRevision(docId, revId, data);
            useDocumentRevisionStore.setState((state) => ({
                revisions: state.revisions.map((rev) =>
                    rev._id === revId ? response.data : rev,
                ),
                loading: false,
            }));
            return response;
        } catch (error) {
            useDocumentRevisionStore.setState({
                error: error.message || 'Reddedilirken hata',
                loading: false,
            });
            throw error;
        }
    }

    async getRevisionPdfBlob(docId, revId, query = {}) {
        const attempts = 2;
        let lastErr;
        for (let i = 0; i < attempts; i += 1) {
            try {
                return await this.service.getRevisionPdfBlob(docId, revId, query);
            } catch (error) {
                lastErr = error;
                const status = error?.response?.status;
                const retryable =
                    status === 502 ||
                    status === 503 ||
                    status === 504 ||
                    error?.code === 'ECONNABORTED';
                if (i < attempts - 1 && retryable) {
                    await new Promise((r) => setTimeout(r, 1200));
                    continue;
                }
                throw error;
            }
        }
        throw lastErr;
    }

    async persistRevisionPdf(docId, revId, query = {}) {
        return this.service.persistRevisionPdf(docId, revId, query);
    }

    async getCompareResultPdfBlob(docId, baseRevId, targetRevId, query = {}) {
        const attempts = 2;
        let lastErr;
        for (let i = 0; i < attempts; i += 1) {
            try {
                return await this.service.getCompareResultPdfBlob(docId, baseRevId, targetRevId, query);
            } catch (error) {
                lastErr = error;
                const status = error?.response?.status;
                const retryable =
                    status === 502 ||
                    status === 503 ||
                    status === 504 ||
                    error?.code === 'ECONNABORTED';
                if (i < attempts - 1 && retryable) {
                    await new Promise((r) => setTimeout(r, 1200));
                    continue;
                }
                throw error;
            }
        }
        throw lastErr;
    }

    async applyTrackChanges(docId, revId, enabled, trackQuery = {}) {
        try {
            const blob = await this.service.applyTrackChanges(docId, revId, enabled, trackQuery);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `tracked-${enabled ? 'on' : 'off'}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(i18next.t('integrationToasts:documentPrepared', { defaultValue: 'Document prepared' }));
            return { success: true };
        } catch (error) {
            toast.error(i18next.t('integrationToasts:couldNotApplyTrackChanges', { defaultValue: 'Could not apply Track changes' }));
            return { success: false, error: error.message };
        }
    }
}
